import './style.css';
import { registerRoutes, initRouter, navigate, setOnSidebarReady, setOnPageHeaderReady } from './infrastructure/router';
import { isAuthenticated } from './infrastructure/auth-guard';
import {
  renderSidebarNavigation,
  initSidebarListeners,
} from './shared/components/sidebar-navigation';
import { renderPageHeader } from './shared/components/page-header';
import { authService, decodeTokenPayload, resolveRoleFromPayload } from './features/auth/auth.service';
import { cartService } from './features/cart/cart.service';
import { eventBus } from './infrastructure/event-bus';
import { storageAdapter } from './infrastructure/storage-adapter';
import { initFeedbackFab } from './features/feedback/feedback-fab';
import { feedbackService } from './features/feedback/feedback.service';
import { customerService } from './features/customers/customer.service';
import { customerDataService } from './features/customers/customer-data.service';
import { userPreferencesService } from './shared/services/user-preferences.service';

// ---------------------------------------------------------------------------
// Route definitions (lazy-loaded page modules)
// ---------------------------------------------------------------------------

registerRoutes([
  {
    path: '/',
    loader: () => import('./features/cart/cart.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Warenkorb',
  },
  {
    path: '/scanner',
    loader: () => import('./features/scanner/scanner.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Scanner',
  },
  {
    path: '/hardware-scanner',
    loader: () => import('./features/hardware-scanner/hardware-scanner.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Hardware-Scanner',
  },
  {
    path: '/manual-entry',
    loader: () => import('./features/manual-entry/manual-entry.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Manuelle Erfassung',
  },
  {
    path: '/login',
    loader: () => import('./features/auth/login.page'),
    layout: 'fullscreen',
    requiresAuth: false,
    title: 'Login',
  },
  {
    path: '/imprint',
    loader: () => import('./features/imprint/imprint.page'),
    layout: 'sidebar',
    requiresAuth: false,
    title: 'Impressum',
  },
  {
    path: '/feedback',
    loader: () => import('./features/feedback/feedback-employee.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Feedback',
  },
  {
    path: '/admin/feedback',
    loader: () => import('./features/admin/feedback-list.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Feedback-Übersicht',
  },
  {
    path: '/customers',
    loader: () => import('./features/customers/customers.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Meine Kunden',
  },
  {
    path: '/chat',
    loader: () => import('./features/chat/chat.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'AI-Assistent',
  },
  {
    path: '/order-simulation',
    loader: () => import('./features/order/order-simulation.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Bestellprüfung',
  },
  {
    path: '/timeline',
    loader: () => import('./features/feature-timeline/feature-timeline.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Feature-Timeline',
  },
  {
    path: '/admin/feature-timeline',
    loader: () => import('./features/feature-timeline/feature-timeline-admin.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Timeline verwalten',
  },
  {
    path: '/admin/statistics',
    loader: () => import('./features/admin/statistics-dashboard.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Statistik-Dashboard',
  },
  {
    path: '/change-password',
    loader: () => import('./features/auth/change-password.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Passwort ändern',
  },
  {
    path: '/settings',
    loader: () => import('./features/settings/settings.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Einstellungen',
  },
  {
    path: '/quick-entry',
    loader: () => import('./features/quick-entry/quick-entry.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Schnellerfassung',
  },
  {
    path: '/assortment',
    loader: () => import('./features/assortment/assortment.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Sortimente',
  },
  {
    path: '/assortment-large',
    loader: () => import('./features/master-data/master-data.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin'],
    title: 'Stammdaten',
  },
  {
    path: '/help',
    loader: () => import('./features/help/help.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Hilfe',
  },
  {
    path: '/help/article',
    loader: () => import('./features/help/help-article.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Hilfe',
  },
  {
    path: '/admin/help',
    loader: () => import('./features/help/help-admin.page'),
    layout: 'sidebar',
    requiresAuth: true,
    title: 'Hilfe-Artikel verwalten',
  },
  // Configurator (Premium Feature – guarded by VITE_FEATURE_CONFIGURATOR_ENABLED)
  {
    path: '/configurator/templates',
    loader: () => import('./features/configurator/pages/templates-list.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'Templates verwalten',
  },
  {
    path: '/configurator/templates/new',
    loader: () => import('./features/configurator/pages/template-editor.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'Neues Template',
  },
  {
    path: '/configurator/templates/edit',
    loader: () => import('./features/configurator/pages/template-editor.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'Template bearbeiten',
  },
  {
    path: '/configurator/templates/visual-builder',
    loader: () => import('./features/configurator/pages/visual-builder.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'Visual Builder',
  },
  {
    path: '/configurator/templates/ai-generate',
    loader: () => import('./features/configurator/pages/ai-generate.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'AI Template-Generator',
  },
  {
    path: '/configurator/pricelists',
    loader: () => import('./features/configurator/pages/pricelists.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'Preislisten verwalten',
  },
  {
    path: '/configurator/exportformats',
    loader: () => import('./features/configurator/pages/exportformats.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee'],
    title: 'Exportformate verwalten',
  },
  // Configurator – Customer-facing pages (all roles)
  {
    path: '/configurator',
    loader: () => import('./features/configurator/pages/configurations-list.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Konfigurationen',
  },
  {
    path: '/configurator/projects',
    loader: () => import('./features/configurator/pages/projects.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Projekte',
  },
  {
    path: '/configurator/projects/detail',
    loader: () => import('./features/configurator/pages/project-detail.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Projekt-Details',
  },
  {
    path: '/configurator/new',
    loader: () => import('./features/configurator/pages/template-selection.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Neue Konfiguration',
  },
  {
    path: '/configurator/edit',
    loader: () => import('./features/configurator/pages/configurator-form.page'),
    layout: 'sidebar',
    requiresAuth: true,
    allowedRoles: ['admin', 'employee', 'customer'],
    title: 'Konfiguration bearbeiten',
  },
]);

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

const app = document.querySelector<HTMLDivElement>('#app');

if (app) {
  // ---------------------------------------------------------------------------
  // SSO Callback Intercept
  // ---------------------------------------------------------------------------
  // Keycloak redirects to /sso/callback?code=...&state=... (real path, no hash).
  // We detect this before the hash-router boots and render the callback page directly.
  if (window.location.pathname === '/sso/callback') {
    void (async () => {
      const { render } = await import('./features/auth/sso-callback.page');
      await render(app);
    })();
  } else {

  void (async () => {
  // Wire up sidebar navigation rendering
  setOnSidebarReady((sidebarEl, _mainEl) => {
    renderSidebarNavigation(sidebarEl);
  });

  // Wire up page header rendering
  setOnPageHeaderReady((mainEl) => {
    renderPageHeader(mainEl);
  });

  // Initialize sidebar event listeners (auth + cart changes)
  initSidebarListeners();

  // -------------------------------------------------------------------------
  // Feedback FAB + online listener
  // -------------------------------------------------------------------------

  initFeedbackFab();
  feedbackService.initOnlineListener();

  // -------------------------------------------------------------------------
  // Cart synchronization with API
  // -------------------------------------------------------------------------

  // Load cart when user is authenticated (login or session restore)
  eventBus.on<{ authenticated: boolean }>('auth-state-changed', async (detail) => {
    if (detail.authenticated) {
      // Determine customerId from multiple sources:
      // 1. User object (from login response or profile)
      // 2. JWT token payload (customerId claim)
      let customerId = '';

      const user = authService.getUser();

      if (user?.customerId) {
        customerId = user.customerId;
      }

      // Try extracting from the JWT token
      if (!customerId) {
        const token = storageAdapter.getAccessToken();
        if (token) {
          const payload = decodeTokenPayload(token);
          if (payload?.customerId || payload?.customer_id) {
            customerId = payload.customerId || payload.customer_id || '';
          }
        }
      }

      // For employees/admins without a customerId in the token,
      // they select customers manually — use their sub/id as fallback for API auth
      if (!customerId) {
        const token = storageAdapter.getAccessToken();
        const payload = token ? decodeTokenPayload(token) : null;
        const role = payload ? resolveRoleFromPayload(payload) : '';
        if (role === 'employee' || role === 'admin') {
          customerId = user?.id || payload?.sub || '';
        }
      }

      // Only load cart and customer data if we have a valid customerId.
      // Users without a customerId (new SSO users) will see the pending overlay.
      if (customerId) {
        // For employees/admins: if a customer was previously selected (stored in session),
        // load that customer's cart instead of the employee's own cart.
        const selectedCustomer = sessionStorage.getItem('snaphub_customer_number');
        const cartCustomerId = selectedCustomer || customerId;

        cartService.loadCart(cartCustomerId).catch(() => {
          // Cart loading failed – user can still use the app, cart will be empty
        });

        // Load customer address data (Auftraggeber + Lieferadressen)
        customerDataService.loadCustomerData(cartCustomerId).catch(() => {
          // Customer data loading failed – address section will show empty state
        });
      }

      // Load customer selection list for employees/admins
      customerService.loadCustomers().catch(() => {
        // Customer list loading failed – selector will show fallback input
      });

      // Load user preferences (feedback widget disabled, etc.)
      userPreferencesService.loadPreferences().catch(() => {
        // Preferences loading failed – defaults will be used
      });
    } else {
      // User logged out – reset local cart state
      cartService.reset();
      customerService.reset();
      customerDataService.reset();
      userPreferencesService.reset();
    }
  });

  // -------------------------------------------------------------------------
  // Session restore BEFORE router init
  // -------------------------------------------------------------------------
  // Restore session first so the auth guard sees a valid access token
  // before the router decides whether to redirect to /login.
  await authService.restoreSessionAsync().then(() => {
    feedbackService.syncOfflineQueue().catch(() => {
      // Sync failed – entries remain in queue for next online event
    });
  }).catch(() => {
    // Session restore failed – user will be redirected to login by auth guard
  });

  // Now init the router – auth state is already resolved
  initRouter({
    outlet: app,
    isAuthenticated,
    onUnauthenticated: () => navigate('/login'),
  });
  })();
  } // end else (non-SSO callback path)
}

// ---------------------------------------------------------------------------
// Service Worker registration with update handling
// ---------------------------------------------------------------------------
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    const { Workbox } = await import('workbox-window');
    const wb = new Workbox('/sw.js');

    let reloadPending = false;

    // When a new SW takes control, reload once to use new assets.
    wb.addEventListener('controlling', () => {
      if (reloadPending) return;
      reloadPending = true;
      window.location.reload();
    });

    wb.addEventListener('waiting', () => {
      // New SW is waiting – activate it immediately
      wb.messageSkipWaiting();
    });

    // Also handle the case where a new SW was already waiting before
    // this page loaded (e.g. user opens app after deployment).
    const reg = await wb.register();
    if (reg?.waiting) {
      wb.messageSkipWaiting();
    }

    // Proactively check for SW updates when the app becomes visible again
    // (e.g. user switches back to the PWA on mobile). This ensures updates
    // are detected without requiring a hard restart.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && reg) {
        reg.update();
      }
    });
  });
}
